/**
 * Size view component.
 * @module components/View/SizeView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Confirm,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent, getContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

// EUR has no live exchange rate of its own yet, so it's shown as a flat
// $5 markup over the USD price.
const formatPrice = (price, currency) => {
  if (!price) return 'N/A';
  return currency === 'EUR' ? `€ ${Number(price) + 5}` : `$ ${price}`;
};
const formatInventory = (inventory) => inventory ?? 0;
const formatWeight = (weight) => (weight ? `${weight} oz` : '');

const fullcode = (content) =>
  content.code_variant
    ? `${content.code}.${content.code_variant}`
    : content.code;

const fullsize = (content) =>
  content.size_variant
    ? `${content.size}${content.size_variant}`
    : content.size;

/**
 * Size view component class.
 * @function SizeView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const SizeView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  const parent = useSelector(
    (state) => state.content.subrequests?.parent?.data,
  );

  useEffect(() => {
    dispatch(getContent(parentUrl, null, 'parent'));
  }, [dispatch, parentUrl]);

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">
          {parent?.category ? parent?.category : ''}
        </div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete size"
        content={`Are you sure you want to delete "${parent?.title} - Size ${fullsize(content)}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="size">
          <h1 className="content-title">
            {parent?.title}
            {' - Size '}
            {fullcode(content)}
            {fullsize(content)}
          </h1>
          <table className="tables-side-by-side">
            <tbody>
              <tr>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="5"
                          textAlign="center"
                          className="general sub rotated"
                        >
                          <span className="rotate-label">General</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="6">Size</TableHeaderCell>
                        <TableCell width="6">
                          {fullcode(content)}
                          {fullsize(content)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Sold separate</TableHeaderCell>
                        <TableCell>
                          {content.sold_separate ? 'Yes' : 'No'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Price</TableHeaderCell>
                        <TableCell>
                          {formatPrice(content.price, currency)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Inventory</TableHeaderCell>
                        <TableCell>
                          {formatInventory(content.inventory)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Weight</TableHeaderCell>
                        <TableCell>{formatWeight(content.weight)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="9"
                          textAlign="center"
                          className="preparation sub rotated"
                        >
                          <span className="rotate-label">Preparation</span>
                        </TableHeaderCell>
                        <TableHeaderCell>Blank</TableHeaderCell>
                        <TableCell>{content.blank}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Name on (degrees)</TableHeaderCell>
                        <TableCell>{content.name_on}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Cutter</TableHeaderCell>
                        <TableCell>{content.cutter}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Diameter</TableHeaderCell>
                        <TableCell>{content.diameter}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Tall</TableHeaderCell>
                        <TableCell>{content.tall}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Wide</TableHeaderCell>
                        <TableCell>{content.wide}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Radius</TableHeaderCell>
                        <TableCell>{content.radius}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Center</TableHeaderCell>
                        <TableCell>{content.center}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Steps</TableHeaderCell>
                        <TableCell>
                          {content.steps
                            ? content.steps
                                .split('\n')
                                .reduce((all, cur) => [...all, <br />, cur])
                            : ''}
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </td>
                <td>
                  <Table celled>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          rowSpan="12"
                          textAlign="center"
                          className="machining sub rotated"
                        >
                          <span className="rotate-label">Detailing</span>
                        </TableHeaderCell>
                        <TableHeaderCell width="6">
                          Turned Diameter
                        </TableHeaderCell>
                        <TableCell width="6">
                          {content.turned_diameter}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Radius Cut</TableHeaderCell>
                        <TableCell>{content.radius_cut}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Length</TableHeaderCell>
                        <TableCell>{content.length}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Width</TableHeaderCell>
                        <TableCell>{content.width}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Angle</TableHeaderCell>
                        <TableCell>{content.angle}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Face</TableHeaderCell>
                        <TableCell>{content.face}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>File</TableHeaderCell>
                        <TableCell>{content.file}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Bit / Burr Size</TableHeaderCell>
                        <TableCell>{content.bit_burr_size}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Mill Size</TableHeaderCell>
                        <TableCell>{content.mill_size}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Depth of Cut</TableHeaderCell>
                        <TableCell>{content.depth_of_cut}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Machine Speed</TableHeaderCell>
                        <TableCell>{content.machine_speed}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell>Head Thickness</TableHeaderCell>
                        <TableCell>{content.head_thickness}</TableCell>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </td>
              </tr>
            </tbody>
          </table>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
SizeView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
};

export default SizeView;
